import { useReducer, useEffect } from "react";
import { useRouteLoaderData, useRevalidator } from "react-router";

import AssignmentApi from "@/api/assignment-api";
import Button from "@/components/common/input/button";
import Toggle from "@/components/common/input/toggle";
import Modal from "@/components/common/modal";
import Spinner from "@/components/common/spinner";
import AssignmentSettingsForm from "@/components/forms/assignment-settings";
import { handled, logRejection } from "@/helpers";
import { showSnackbar } from "@/hooks/snackbar";
import { usePageTitle } from "@/hooks/title";
import type { AssignmentLoaderData, SharingSettings } from "@/types/assignment";

type SettingId = keyof SharingSettings;

interface SettingsItem {
  id: SettingId;
  label: string;
  description: string;
  checked: boolean;
}

interface SettingsState {
  is_syncing: boolean;
  settings_items: SettingsItem[];
}

type SettingsAction =
  | { type: "set-syncing-status"; payload: boolean }
  | { type: "update-checked-state"; payload: SharingSettings | undefined };

const AssignmentSettings = () => {
  const [state, dispatch] = useReducer(reducer, initial_state);
  const { assignment } = useRouteLoaderData("single-assignment") as AssignmentLoaderData;
  const revalidator = useRevalidator();
  usePageTitle("Settings");

  useEffect(() => {
    // sync server state with UI
    dispatch({
      type: "update-checked-state",
      payload: assignment?.settings?.sharing_settings,
    });
  }, [assignment]);

  const _update = () => {
    void revalidator.revalidate();
  };

  const _handleSettingChange = ({ id, checked }: { id: SettingId; checked: boolean }) => {
    const abort_controller = new AbortController();
    const updated_settings_items = markChecked(state.settings_items, id, checked);
    const label = state.settings_items.find((item) => item.id === id)?.label ?? id;
    AssignmentApi.updateSharingSettings(
      assignment.id,
      updated_settings_items,
      abort_controller.signal,
    )
      .then(({ error }) => {
        if (error) {
          console.error(error);
          // The toggle is driven by the revalidated assignment, so a failure
          // leaves it where it was and the click reads as having done nothing.
          showSnackbar(`Could not save the "${label}" setting.`, "warning");
          return;
        }
        void revalidator.revalidate();
      })
      .catch(logRejection);
  };

  async function _manualSync() {
    dispatch({ type: "set-syncing-status", payload: true });
    const { error } = await AssignmentApi.syncCandidates({
      assignment_id: assignment.id,
    });
    dispatch({ type: "set-syncing-status", payload: false });
    if (error) {
      console.error(error);
      showSnackbar("Could not sync candidates.", "warning");
      return;
    }
    void revalidator.revalidate();
  }

  return (
    <div className="mx-auto max-w-[60rem]">
      <div className="mb-8 flex gap-4">
        <Modal>
          <Button>Change name</Button>
          <AssignmentSettingsForm assignment={assignment} callbackFn={_update} />
        </Modal>
        <Button onClick={handled(_manualSync)} disabled={state.is_syncing}>
          {state.is_syncing ? <Spinner /> : "Manual sync"}
        </Button>
      </div>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-500">Sharing settings</h2>
        <p>Controls what&apos;s visible for assessors</p>
      </div>
      <div className="grid grid-cols-2">
        {state.settings_items.map((dp) => {
          return (
            <div key={dp.id} className="mb-2 flex items-center gap-4">
              <Toggle
                onChange={() => _handleSettingChange({ id: dp.id, checked: !dp.checked })}
                checked={dp.checked}
                aria-label={dp.label}
              />
              <p>
                {dp.label}
                <br />
                <span className="text-sm text-gray-400">{dp.description}</span>
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const reducer = (state: SettingsState, action: SettingsAction): SettingsState => {
  switch (action.type) {
    case "set-syncing-status": {
      return { ...state, is_syncing: action.payload };
    }
    case "update-checked-state": {
      // The stored blob is the whole truth, so a key it omits is off. The
      // earlier merge only ever turned an item on, and read as correct only
      // because markChecked was writing the off state into this array in place.
      const { payload } = action;
      const updated_settings_items = state.settings_items.map((setting) => ({
        ...setting,
        checked: Boolean(payload?.[setting.id]),
      }));
      return { ...state, settings_items: updated_settings_items };
    }
  }
};

const markChecked = (settings: SettingsItem[], id: SettingId, checked: boolean) => {
  return transformToSettingsItemObject(
    settings.map((setting) => (setting.id === id ? { ...setting, checked } : setting)),
  );
};

const transformToSettingsItemObject = (settings: SettingsItem[]): SharingSettings => {
  // transforms into shape for backend
  const settings_items: SharingSettings = {};
  for (const setting of settings) {
    settings_items[setting.id] = setting.checked;
  }
  return settings_items;
};

const initial_state: SettingsState = {
  is_syncing: false,
  settings_items: [
    {
      id: "contact_details",
      label: "Contact details",
      description: "Email address and phone number",
      checked: false,
    },
    {
      id: "url",
      label: "URL",
      description: "In most cases this is a LinkedIn URL",
      checked: false,
    },
    {
      id: "role",
      label: "Role",
      description: "The candidate's role",
      checked: false,
    },
    {
      id: "organization",
      label: "Organization",
      description: "Where the candidate works",
      checked: false,
    },
    {
      id: "comments",
      label: "Comments",
      description: "Comments made on the candidate",
      checked: false,
    },
    {
      id: "action_logs",
      label: "Action logs",
      description: "Actions logs from Ponty",
      checked: false,
    },
    {
      id: "birthyear",
      label: "Birthyear",
      description: "The candidate's birthyear",
      checked: false,
    },
    // {
    //   id: "process_step",
    //   label: "Process step",
    //   description: "Where in the process the candidate is",
    //   checked: false,
    // },
    {
      id: "salary",
      label: "Salary",
      description: "Salary claim",
      checked: false,
    },
    // {
    //   id: "evali",
    //   label: "evali Results",
    //   description: "Test results from evali",
    //   checked: false,
    // },
  ],
};

export default AssignmentSettings;
